// ============================================================================
// Recurring Checklists — singleton-item model.
// ----------------------------------------------------------------------------
// Master records live in `checklist_items` with `checklistType` = 'MAIN' |
// 'DAILY' | 'WEEKLY'. Every editable field (title, weight, priority, position,
// attachments, comments…) lives on the master item — occurrences never fork.
// Only `isCompleted` and `workflowStatus` are tracked per-occurrence, via
// `ChecklistItem.occurrences[key]`.
//
// Legacy page collections (`daily_pages`, `weekly_pages`) are retired; the
// legacy types below stay in this file only long enough for the one-shot
// migration to read them and collapse their state into the master items.
// ============================================================================

export type ChecklistType = 'INDIVIDUAL' | 'DAILY' | 'WEEKLY'
export type RecurringMode = 'DAILY' | 'WEEKLY'
export type PageOccurrenceKey = string // ISO date (Daily) or ISO week (Weekly)

export type RetentionMode = 'PRUNE_AFTER_N' | 'KEEP_FOREVER'

export const DEFAULT_RECURRING_WINDOW_DAYS = 3

export const normalizeRecurringWindowDays = (_windowDays?: number | null): number =>
  DEFAULT_RECURRING_WINDOW_DAYS

/** Per-workspace singleton. Doc id = workspaceId. In the singleton model this
 *  now controls occurrence-map retention (how many old keys to keep) and the
 *  "migrated" flag flipped after the one-shot legacy purge succeeds. */
export type WorkspaceRecurringSettings = {
  id: string
  workspaceId: string
  retentionMode: RetentionMode
  retentionDays: number
  /** DEPRECATED (parent-scoped refactor). Recurring items now materialize
   *  from parent.startDate → parent.endDate, so this global rolling window
   *  is no longer consulted. Field retained for legacy doc deserialization
   *  only; do not read. */
  recurringWindowDays?: number
  /** Lead time (in days) applied to the derived time-status transition:
   *  a DURATION checklist becomes 'active' `todosLeadDays` before its
   *  scheduleStartDate; a DATED checklist likewise. Default 0. Admin
   *  editable via a settings surface (not yet built). */
  todosLeadDays?: number
  /** How many completed windows back from today still render as "completed".
   *  Anything older renders as "archived" (client-side rendering rule only —
   *  no DB state change). Range 1-5; default 2.
   *
   *  Example: in Weekly mode with archiveThresholdWindows = 2, the current
   *  week and the two previous weeks show completions as "completed"; the
   *  third-oldest week and beyond render the same rows as "archived" even
   *  though `workflowStatus` on those items is still COMPLETED. */
  archiveThresholdWindows?: number
  singletonMigrationCompletedAt?: string | null
  createdAt?: unknown
  updatedAt?: unknown
  recordVersion: number
  // Legacy fields — window sizes are no longer used (no page docs to
  // materialize a window into) but stay in the type so older docs deserialize.
  dailyWindowSize?: number
  weeklyWindowSize?: number
  lastRolledIso?: string | null
}

// ---------------------------------------------------------------------------
// LEGACY types — read-only, migration input only. Do not create new writes
// against these; the migration deletes their backing docs after collapse.
// ---------------------------------------------------------------------------

/** DEPRECATED — read only for migration. */
export type PageNodeStub = {
  nodeId: string
  position: { x: number; y: number }
  isCompleted?: boolean
  workflowStatus?: string | null
  isDiverged?: boolean
}

/** DEPRECATED — read only for migration. */
export type RecurringPage = {
  id: string
  workspaceId: string
  mode: RecurringMode
  occurrenceKey: PageOccurrenceKey
  pageDateIso: string
  nodes: PageNodeStub[]
  mirrorSourcePageId?: string | null
  createdAt?: unknown
  updatedAt?: unknown
  recordVersion: number
}
